/**
 * The main workflow: choose a file and a rule set, watch progress, read results.
 *
 * Single-file and batch share this view because the inputs are otherwise
 * identical; batch is a toggle, not a separate journey.
 */

import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import type {
  AppConfig,
  IdsDocumentSummary,
  JobResult,
  JobStatus,
  TemplateSummary,
  ValidationReport,
} from '../lib/types';
import { Dropzone } from './Dropzone';
import type { ViewerFinding } from './IfcViewer';
import { ReportView } from './ReportView';

// three + web-ifc-three add several MB even minified. Splitting this into its
// own chunk means everyone validating a file pays only for the report; the
// viewer's weight is downloaded solely by whoever actually sees one rendered.
const IfcViewer = lazy(() => import('./IfcViewer').then((m) => ({ default: m.IfcViewer })));
import {
  Badge,
  Callout,
  ProgressBar,
  ShieldIcon,
  Spinner,
  StatusBadge,
  formatBytes,
} from './primitives';

interface ValidateViewProps {
  config: AppConfig;
  templates: TemplateSummary[];
  idsDocuments: IdsDocumentSummary[];
  onValidated: () => void;
}

type Phase = 'idle' | 'uploading' | 'running' | 'done' | 'error';

export function ValidateView({
  config,
  templates,
  idsDocuments,
  onValidated,
}: ValidateViewProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [templateId, setTemplateId] = useState('iso19650-baseline');
  const [idsFiles, setIdsFiles] = useState<File[]>([]);
  const [savedIdsId, setSavedIdsId] = useState('');
  const [batchMode, setBatchMode] = useState(false);
  const [batchName, setBatchName] = useState('');

  const [phase, setPhase] = useState<Phase>('idle');
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [result, setResult] = useState<JobResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [upsell, setUpsell] = useState(false);
  const [warmingUp, setWarmingUp] = useState(false);
  const [activeReport, setActiveReport] = useState(0);
  const [downloading, setDownloading] = useState<'pdf' | 'csv' | 'bcf' | null>(null);

  const canUseIds = config.features.ids_validation;
  const canBatch = config.features.batch_processing;

  useEffect(() => {
    if (!canBatch && batchMode) setBatchMode(false);
  }, [batchMode, canBatch]);

  const reports: ValidationReport[] = result?.reports ?? [];
  const report = reports[activeReport];

  const busy = phase === 'uploading' || phase === 'running';

  const idsLabel = useMemo(() => {
    if (idsFiles[0]) return idsFiles[0].name;
    const saved = idsDocuments.find((document) => document.id === savedIdsId);
    return saved?.name ?? null;
  }, [idsDocuments, idsFiles, savedIdsId]);

  async function run() {
    if (!files.length) return;
    setPhase('uploading');
    setError(null);
    setUpsell(false);
    setWarmingUp(false);
    setResult(null);
    setStatus(null);
    setActiveReport(0);

    try {
      const started =
        batchMode && files.length > 1
          ? await api.startBatch({
              files,
              templateId,
              idsFile: idsFiles[0] ?? null,
              idsId: savedIdsId || null,
              name: batchName || undefined,
            })
          : await api.startValidation({
              file: files[0] as File,
              templateId,
              idsFile: idsFiles[0] ?? null,
              idsId: savedIdsId || null,
            });

      setStatus(started);
      setPhase('running');
      const finished = await api.pollJob(started.job_id, setStatus);
      setResult(finished);
      setPhase('done');
      onValidated();
    } catch (caught) {
      const warmingUp = caught instanceof ApiError && caught.isServiceWarmingUp;
      const message = warmingUp
        ? "The server was idle and is still starting up — this can take a few seconds. Click Validate again to retry."
        : caught instanceof ApiError
          ? caught.message
          : 'Something went wrong. Please try again.';
      setError(message);
      setWarmingUp(warmingUp);
      setUpsell(caught instanceof ApiError && caught.isPaymentRequired);
      setPhase('error');
    }
  }

  async function download(format: 'pdf' | 'csv' | 'bcf') {
    if (!result) return;
    setDownloading(format);
    try {
      await api.downloadJobReport(
        result.job_id,
        format,
        reports.length > 1 ? activeReport : undefined,
      );
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'The download failed.');
    } finally {
      setDownloading(null);
    }
  }

  async function emailReport(input: { recipientEmail: string; message: string; includeBcf: boolean }) {
    if (!result) return;
    await api.emailJobReport(
      result.job_id,
      input,
      reports.length > 1 ? activeReport : undefined,
    );
  }

  function reset() {
    setFiles([]);
    setIdsFiles([]);
    setResult(null);
    setStatus(null);
    setError(null);
    setPhase('idle');
  }

  // ---------------------------------------------------------------- results
  if (phase === 'done' && report) {
    return (
      <div className="stack">
        <div className="row">
          <button type="button" className="btn" onClick={reset}>
            ← Validate another file
          </button>
          {reports.length > 1 && (
            <>
              <div className="masthead-spacer" />
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => result && api.downloadJobReport(result.job_id, 'csv')}
              >
                Consolidated CSV for all {reports.length} files
              </button>
            </>
          )}
        </div>

        {reports.length > 1 && (
          <div className="card">
            <div className="card-head">
              <h3>Batch results</h3>
              <div className="masthead-spacer" />
              <span className="hint">
                {reports.filter((r) => r.passed).length} of {reports.length} files pass
              </span>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Result</th>
                    <th>Findings</th>
                    <th>Schema</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {reports.map((item, index) => (
                    <tr key={item.file.filename + index}>
                      <td>{item.file.filename}</td>
                      <td>
                        <StatusBadge
                          status={
                            item.verdict === 'pass'
                              ? 'pass'
                              : item.verdict === 'pass_with_warnings'
                                ? 'warning'
                                : 'fail'
                          }
                        />
                      </td>
                      <td>{item.total_findings.toLocaleString()}</td>
                      <td className="cell-mono">{item.file.schema ?? '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => setActiveReport(index)}
                          disabled={index === activeReport}
                        >
                          {index === activeReport ? 'Showing' : 'View'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <ReportView
          report={report}
          onDownload={download}
          downloading={downloading}
          watermarked={config.watermark_reports}
          onEmail={config.features.ids_validation ? emailReport : undefined}
        />

        {files[activeReport] && (
          <Suspense
            fallback={
              <div className="card">
                <div className="card-body row">
                  <Spinner /> <span className="hint-strong">Loading 3D preview…</span>
                </div>
              </div>
            }
          >
            <IfcViewer
              // Remounts per file: a fresh Three.js scene per model is simpler
              // and safer than trying to mutate one in place across files.
              key={`${files[activeReport]!.name}-${files[activeReport]!.size}-${activeReport}`}
              file={files[activeReport]!}
              findings={findingsForViewer(report)}
              // Free/anonymous callers get the full model as the "look what you
              // get for free" hook; signed-in callers get a fast, targeted view
              // of just what needs fixing.
              mode={config.authenticated ? 'highlights' : 'full'}
            />
          </Suspense>
        )}
      </div>
    );
  }

  // ------------------------------------------------------------------ form
  return (
    <div className="grid grid-2-1">
      <div className="stack">
        <div className="card">
          <div className="card-head">
            <h2>Validate an IFC file</h2>
          </div>
          <div className="card-body">
            <div className="grid grid-1-1" style={{ gap: '1.5rem' }}>
              {/* left column: IFC upload, then rule set directly below it --
                  one grid cell so the column's own content height drives
                  where "Rule set" starts, instead of being stretched to
                  match the (taller) right column via row auto-sizing. */}
              <div className="stack" style={{ gap: '1.5rem' }}>
              <div className="stack" style={{ gap: '0.6rem' }}>
                <div className="section-divider no-border">
                  <span>{batchMode ? 'Upload IFC files' : 'Upload an IFC file'}</span>
                  {canBatch ? (
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={batchMode}
                        onChange={(event) => setBatchMode(event.target.checked)}
                        disabled={busy}
                      />
                      Batch mode
                    </label>
                  ) : (
                    <Badge>Batch is a paid feature</Badge>
                  )}
                </div>
                <Dropzone
                  files={files}
                  onChange={setFiles}
                  accept={config.accepted_extensions}
                  multiple={batchMode}
                  maxBytes={config.max_upload_bytes}
                  disabled={busy}
                  label={batchMode ? 'Drop IFC files here' : 'Drop an IFC file here'}
                  hint={
                    batchMode
                      ? `Up to ${config.max_batch_files} files, validated against the same rule set.`
                      : undefined
                  }
                />
                {batchMode && (
                  <label className="field">
                    <span>Batch name (optional)</span>
                    <input
                      type="text"
                      value={batchName}
                      onChange={(event) => setBatchName(event.target.value)}
                      placeholder="e.g. Stage 4 submission — 12 Aug"
                    />
                  </label>
                )}
              </div>

              {/* bottom-left: rule set, directly below the IFC dropzone in
                  the same column -- collapsed to headings, the selected
                  option alone expands to show its description */}
              <div className="stack" style={{ gap: '0.6rem' }}>
                <div className="section-divider no-border">
                  <span>Rule set</span>
                  <span className="hint">What the model is checked against</span>
                </div>
                <div className="option-list option-list-compact">
                  {templates.map((template) => {
                    const isSelected = templateId === template.id;
                    return (
                      <label
                        key={template.id}
                        className={`option${isSelected ? ' selected' : ''}${
                          template.available ? '' : ' disabled'
                        }`}
                      >
                        <input
                          type="radio"
                          name="template"
                          value={template.id}
                          checked={isSelected}
                          disabled={!template.available || busy}
                          onChange={() => setTemplateId(template.id)}
                        />
                        <div className="option-body">
                          <div className="option-name">
                            {template.name}{' '}
                            {!template.builtin && <Badge>Saved</Badge>}{' '}
                            {!template.available && <Badge>Professional</Badge>}
                          </div>
                          {isSelected && <div className="option-desc">{template.description}</div>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
              </div>

              {/* right column: IDS upload, then status/Validate directly
                  below it -- same one-cell-per-column structure as the left
                  column, for the same reason. */}
              <div className="stack" style={{ gap: '1.5rem' }}>
              {/* top-right: IDS upload. Free-tier callers see the real
                  dropzone and saved-spec picker, not a swapped-out upsell --
                  greyed out (via the existing `disabled` styling on
                  Dropzone/select) with a hover tooltip pointing at getting a
                  licence, so the feature is visibly there rather than
                  hidden. */}
              <div
                className="stack"
                style={{ gap: '0.6rem' }}
                title={
                  canUseIds
                    ? undefined
                    : 'IDS validation needs a Professional licence. Get an API key at clearifc.com/pricing to unlock it.'
                }
              >
                <div className="section-divider no-border">
                  <span>IDS specification</span>
                  {canUseIds ? (
                    <span className="hint">Optional</span>
                  ) : (
                    <Badge>Professional</Badge>
                  )}
                </div>
                <div
                  style={!canUseIds ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
                  aria-disabled={!canUseIds}
                >
                  <Dropzone
                    files={idsFiles}
                    onChange={(next) => {
                      setIdsFiles(next);
                      if (next.length) setSavedIdsId('');
                    }}
                    accept={['.ids', '.xml', '.idsx']}
                    disabled={!canUseIds || busy}
                    label="Drop an IDS file here"
                    hint="buildingSMART Information Delivery Specification (.ids, .xml, or a locked .idsx)."
                  />
                </div>
                {!canUseIds && (
                  <span className="hint">
                    Professional feature — hover for how to unlock it. Free-tier users can
                    still inspect an IDS file under the IDS tab to see what it asks for.
                  </span>
                )}
                {canUseIds && idsDocuments.length > 0 && (
                  <label className="field">
                    <span>Or use a saved specification</span>
                    <select
                      value={savedIdsId}
                      onChange={(event) => {
                        setSavedIdsId(event.target.value);
                        if (event.target.value) setIdsFiles([]);
                      }}
                      disabled={busy}
                    >
                      <option value="">None</option>
                      {idsDocuments.map((document) => (
                        <option key={document.id} value={document.id}>
                          {document.name} ({document.specification_count} specifications)
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {idsLabel && <span className="hint-strong">Will validate against: {idsLabel}</span>}
              </div>

              {/* bottom-right: status and the Validate button */}
              <div className="stack" style={{ gap: '0.6rem', justifyContent: 'flex-end' }}>
                {busy && (
                  <div className="stack" style={{ gap: '0.5rem' }}>
                    <ProgressBar percent={status?.progress ?? 0} />
                    <div className="row">
                      <Spinner />
                      <span className="hint-strong">
                        {status?.message ?? 'Uploading…'}
                        {status?.current_file ? ` — ${status.current_file}` : ''}
                      </span>
                      <div className="masthead-spacer" />
                      <span className="hint">{status?.progress ?? 0}%</span>
                    </div>
                    {status && status.file_count > 1 && (
                      <span className="hint">
                        File {Math.min(status.completed_files + 1, status.file_count)} of{' '}
                        {status.file_count}
                      </span>
                    )}
                  </div>
                )}

                {error && (
                  <Callout variant={upsell ? 'upsell' : warmingUp ? 'info' : 'error'}>
                    <strong>
                      {upsell
                        ? 'Professional feature'
                        : warmingUp
                          ? 'Server starting up'
                          : 'Validation could not start'}
                    </strong>
                    <div style={{ marginTop: '0.2rem' }}>{error}</div>
                    {warmingUp && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ marginTop: '0.6rem' }}
                        onClick={run}
                        disabled={!files.length || busy}
                      >
                        {busy ? 'Retrying…' : 'Try again'}
                      </button>
                    )}
                  </Callout>
                )}

                <button
                  type="button"
                  className="btn btn-primary btn-block"
                  onClick={run}
                  disabled={!files.length || busy}
                >
                  {busy ? 'Validating…' : batchMode ? `Validate ${files.length} files` : 'Validate'}
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="stack">
        <div className="card">
          <div className="card-head">
            <ShieldIcon />
            <h3>Your model is not stored</h3>
          </div>
          <div className="card-body stack" style={{ gap: '0.6rem' }}>
            <p className="hint">
              The file is streamed to temporary storage, parsed, and deleted the moment the
              report exists — whether the validation succeeded or failed. No geometry is
              retained.
            </p>
            <p className="hint">
              Only the report itself is kept, and only if you are signed in: verdict,
              per-check summaries, and the list of failing elements by GlobalId.
              {config.features.history &&
                ` History is held for ${config.history_retention_days} days.`}
            </p>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>What gets checked</h3>
          </div>
          <div className="card-body">
            <div className="meta-list">
              {[
                ['Schema conformance', 'Parses cleanly and matches its declared IFC schema'],
                ['Spatial structure', 'Project → site → building → storey, and containment'],
                ['Property sets', 'Required psets and properties per the rule set'],
                ['GUID integrity', 'Duplicate and malformed GlobalIds'],
                ['Geometry', 'Missing, empty, unbuildable or zero-volume solids'],
                ['Classification', 'Uniclass and other classification coverage'],
              ].map(([title, description]) => (
                <div className="meta-row" key={title}>
                  <span className="meta-key">{title}</span>
                  <span className="hint">{description}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>Limits on your plan</h3>
          </div>
          <div className="card-body">
            <div className="meta-list">
              <div className="meta-row">
                <span className="meta-key">Maximum file size</span>
                <span className="meta-value">{formatBytes(config.max_upload_bytes)}</span>
              </div>
              <div className="meta-row">
                <span className="meta-key">Batch processing</span>
                <span className="meta-value">
                  {canBatch ? `Up to ${config.max_batch_files} files` : 'Not on the free tier'}
                </span>
              </div>
              <div className="meta-row">
                <span className="meta-key">IDS validation</span>
                <span className="meta-value">{canUseIds ? 'Included' : 'Professional tier'}</span>
              </div>
              <div className="meta-row">
                <span className="meta-key">PDF reports</span>
                <span className="meta-value">
                  {config.watermark_reports ? 'Watermarked' : 'Unbranded'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Flattens every check's findings into the shape the 3D viewer needs. */
function findingsForViewer(report: ValidationReport): ViewerFinding[] {
  const findings: ViewerFinding[] = [];
  for (const check of report.checks) {
    for (const finding of check.findings) {
      if (!finding.element?.global_id) continue; // nothing to locate in the geometry
      findings.push({
        global_id: finding.element.global_id,
        severity: finding.severity,
        ifc_type: finding.element.ifc_type,
        name: finding.element.name,
        message: finding.message,
      });
    }
  }
  return findings;
}
